import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import { Check } from "lucide-react";
import * as React from "react";
import { cn } from "@/lib/utils";

// ─────────────────────────────────────────────
// Checkbox
//
// Design: §6.6 Selection Controls
// ─────────────────────────────────────────────

const Checkbox = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>
>(({ className, ...props }, ref) => (
  <CheckboxPrimitive.Root
    ref={ref}
    className={cn(
      "peer h-4 w-4 shrink-0 rounded-chip border border-border-default bg-bg-input",
      "transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-1 focus-visible:ring-offset-page",
      "disabled:cursor-not-allowed disabled:opacity-50",
      "data-[state=checked]:border-accent data-[state=checked]:bg-accent",
      className
    )}
    {...props}
  >
    <CheckboxPrimitive.Indicator className="flex items-center justify-center text-white">
      <Check className="h-3 w-3" strokeWidth={3} />
    </CheckboxPrimitive.Indicator>
  </CheckboxPrimitive.Root>
));
Checkbox.displayName = CheckboxPrimitive.Root.displayName;

// ── CheckboxWithLabel ────────────────────────
interface CheckboxWithLabelProps
  extends React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root> {
  label: string;
  description?: string;
}

function CheckboxWithLabel({ label, description, id, className, ...props }: CheckboxWithLabelProps) {
  const checkId = id ?? label.toLowerCase().replace(/\s+/g, "-");
  return (
    <div className={cn("flex items-start gap-2.5", className)}>
      <Checkbox id={checkId} {...props} />
      <div className="flex flex-col gap-0.5">
        <label
          htmlFor={checkId}
          className="cursor-pointer text-[14px] font-medium text-text-secondary peer-disabled:cursor-not-allowed peer-disabled:opacity-50"
        >
          {label}
        </label>
        {description && (
          <p className="text-caption text-text-muted">{description}</p>
        )}
      </div>
    </div>
  );
}

export { Checkbox, CheckboxWithLabel };
